import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import type { PartInfo } from "../../models";

interface Props {
  open: boolean;
  containerName: string;
  onClose: () => void;
  onConfirm: (partInfo: PartInfo) => void;
  /** Whether to show Make/Model/Vendor fields. Defaults to true (EAF). Set false for Caster. */
  showMakeModelVendor?: boolean;
  /** Whether to show Serial Number field. Defaults to true. Caster only shows for Copper Mold Tube. */
  showSerialNumber?: boolean;
  /** Whether to show Warehouse/Drawing Number fields. Defaults to false. Only Caster Mold uses these. */
  showWarehouseDrawing?: boolean;
  /** Label for an optional Part Name field (e.g., "Mold Name"). When provided, the field is shown. */
  partNameLabel?: string;
}

export const NewSparePartModal = ({
  open,
  containerName,
  onClose,
  onConfirm,
  showMakeModelVendor = true,
  showSerialNumber = true,
  showWarehouseDrawing = false,
  partNameLabel,
}: Props) => {
  // New part fields
  const [partName, setPartName] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [vendor, setVendor] = useState("");
  const [warehouseNumber, setWarehouseNumber] = useState("");
  const [drawingNumber, setDrawingNumber] = useState("");

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setPartName("");
      setSerialNumber("");
      setMake("");
      setModel("");
      setVendor("");
      setWarehouseNumber("");
      setDrawingNumber("");
    }
  }, [open]);

  const handleConfirm = () => {
    const partInfo: PartInfo = {
      PartName: partName || "",
      Make: make || "",
      Model: model || "",
      Vendor: vendor || "",
      SerialNumber: serialNumber || "",
      WarehouseNumber: warehouseNumber || "",
      DrawingNumber: drawingNumber || "",
    };
    onConfirm(partInfo);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Spare Part - {containerName}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {partNameLabel ? (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={partNameLabel}
                value={partName}
                onChange={(e) => setPartName(e.target.value)}
              />
            </Grid>
          ) : null}
          {showSerialNumber ? (
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Serial Number"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
              />
            </Grid>
          ) : null}
          {showMakeModelVendor ? (
            <>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Make"
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Vendor"
                  value={vendor}
                  onChange={(e) => setVendor(e.target.value)}
                />
              </Grid>
            </>
          ) : null}
          {showWarehouseDrawing ? (
            <>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Warehouse Number"
                  value={warehouseNumber}
                  onChange={(e) => setWarehouseNumber(e.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Drawing Number"
                  value={drawingNumber}
                  onChange={(e) => setDrawingNumber(e.target.value)}
                />
              </Grid>
            </>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
